import { useMemo } from "react";
import { useIntl } from "react-intl";
import { useDispatch } from "react-redux";
import { searchNotifications } from "../../src/api/notifications";
import { showBackendMessage } from "../../src/helpers/messagesHelper";
import { useAuth } from "../../src/hooks/useAuth";
import { FetchFunctionResult, useFetch } from "../../src/hooks/useFetch";
import { SortOrder } from "../../src/hooks/useTableSort";
import Messages from "../../src/localization/Messages";
import { INotification } from "../../src/models/notification";
import { searchParamsInit } from "../../src/models/searchParams";
import { ApiEndpoints, Roles } from "../../src/models/userData";

export type useFetchNotificationsProps = {
    limit?: number;
    filter?: object;
    sortOrder?: SortOrder;
};

export function useFetchNotifications({
    limit = 0,
    filter = {},
    sortOrder = ["createTs desc"],
}: useFetchNotificationsProps): FetchFunctionResult<INotification[]> {
    const intl = useIntl();
    const dispatch = useDispatch();

    const { user } = useAuth();
    const userRoles = user.roles;
    const isDispatcher = userRoles.includes(Roles.PASSENGER_CABIN_CRUISES_DISPATCHER);
    const isDocks = userRoles.includes(Roles.LOCKS_AND_DOCKS_DISPATCHER);

    const apiEndpoint = useMemo(() => {
        const endpoint = isDispatcher ? ApiEndpoints.dispatcher : isDocks ? ApiEndpoints.docks : "";
        return endpoint;
    }, [isDispatcher, isDocks]);

    const propsJsonString = JSON.stringify({ limit, filter, order: sortOrder });


    const { data, loading, refetch } = useFetch<INotification[]>({
        fetchFn: async () => {
            if (!apiEndpoint) {
                return [];
            }
            try {
                const res = await searchNotifications(
                    { ...searchParamsInit, ...JSON.parse(propsJsonString) },
                    apiEndpoint,
                );
                return res.data.rows ?? [];
            } catch (e) {
                console.log(e);
                dispatch(showBackendMessage(intl, "error", "fetching", Messages.notifications));
                return [];
            } finally {
                // NOOP
            }
        },
        fetchDeps: [propsJsonString, apiEndpoint],
    });

    return { data: data ?? [], loading, refetch };
}
